import Button from "../../../../../components/shared/Button/Button";
import moment from "moment";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { DateRangePicker } from "react-date-range";
import { FiChevronDown } from "react-icons/fi";
import { twMerge } from 'tailwind-merge';

export function SelectDateRange({
  disabled,
  onChange,
  onClear,
  placeholder,
  values,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);

  const start = useMemo(
    () => (values?.startDate ? toDate(values.startDate) : null),
    [values]
  );
  const end = useMemo(
    () => (values?.endDate ? toDate(values.endDate) : null),
    [values]
  );

  const summary = useMemo(() => {
    if (!start || !end) return placeholder;
    return `${moment(start).format("MMM Do")} - ${moment(end).format(
      "MMM Do"
    )}`;
  }, [start, end, placeholder]);

  const baseRange = useMemo(() => {
    if (start && end)
      return {
        endDate: end,
        key: values?.key || "selection",
        startDate: start,
      };
    const today = new Date();
    return { endDate: today, key: "selection", startDate: today };
  }, [start, end, values?.key]);

  const handleToggle = () => {
    if (!disabled) setOpen((o) => !o);
  };

  const close = () => setOpen(false);

  // outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // esc close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className={twMerge("relative")} ref={rootRef}>
      <Button
        className="flex flex-row items-center gap-2"
        color={values ? "primary50" : "gray100"}
        isDisabled={disabled}
        onClick={handleToggle}
        ref={buttonRef}
        rounded="full"
        size="sm"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {summary}
        <FiChevronDown
          className={twMerge(
            "shrink-0 transition-transform",
            open && "rotate-180"
          )}
        />
      </Button>

      <AnimatePresence>
        {open && !disabled && (
          <motion.div
            role="dialog"
            aria-modal="true"
            className="absolute z-20 mt-2"
            initial={{ opacity: 0, scale: 0.98, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -4 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
          >
            <div className="overflow-hidden rounded-xl border border-gray200 bg-white shadow-xl">
              <DateRangePicker
                direction="horizontal"
                editableDateInputs={false}
                inputRanges={[]}
                moveRangeOnFirstSelection={false}
                ranges={[baseRange]}
                onChange={(payload) => {
                  const range = normalizeChangePayload(payload);
                  const next = {
                    endDate: toDate(range.endDate),
                    key: range.key || "selection",
                    startDate: toDate(range.startDate),
                  };
                  onChange(next);
                }}
              />

              <div className="flex items-center justify-end gap-4 border-t border-gray200 px-6 py-3">
                <button
                  type="button"
                  onClick={() => onClear?.()}
                  className={twMerge(
                    "text-sm font-semibold",
                    values
                      ? "text-error600 hover:underline"
                      : "cursor-default text-gray400"
                  )}
                >
                  Clear
                </button>
                <button
                  onClick={() => {
                    close();
                    buttonRef.current?.focus();
                  }}
                  className="text-sm text-gray700 hover:underline"
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// --- helpers ---
function normalizeChangePayload(payload) {
  if (payload?.selection) return payload.selection;
  if (payload?.date) return payload.date;
  if (payload?.startDate && payload?.endDate) return payload;
  const today = new Date();
  return { startDate: today, endDate: today, key: "selection" };
}

function toDate(x) {
  const d = x instanceof Date ? x : new Date(x);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

export default SelectDateRange;
